import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import axios from '../../axios';
import { Menu, Paper } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../AuthContext';

// Function to format date
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Function to format percentage with fallback value
const formatPercentage = (value) => {
  if (value == null || value === '' || isNaN(parseFloat(value))) {
    return '--'; // Return -- if the value is invalid or missing
  }
  return `${parseFloat(value).toFixed(2)}%`; // Formats to 2 decimal places
};

// Function to format text with fallback value
const formatText = (value) => {
  return value == null || value === '' ? '--' : value;
};

const formatPlacementType = (value) => {
  if (value === null || value === undefined || value === '') {
    return ''; // Return an empty string if the data is not present
  }
  return value === 'Yes' ? 'Self Employed' : 'Leased Employed';
};
const Example = ({ updateProductModalSetting, updateAttendanceSetting, updatePlacementSetting, handlePageUpdate }) => {
  const navigate = useNavigate();
  const { centre,userType } = useContext(AuthContext);
  const location = useLocation();
  const { id } = location.state || {};


  const columns = useMemo(
    () => [
      { accessorKey: 'fullname', header: 'Name', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'upsdm_id', header: 'Candidate ID', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'attendee_id', header: 'SIP ID', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'gender', header: 'Gender', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'aadhar_no', header: 'Aadhar No', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'email', header: 'Email', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'caste', header: 'Caste', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'dob', header: 'DOB', Cell: ({ cell }) => formatDate(cell.getValue()) },
      { accessorKey: 'guardian_name', header: 'Guardian Name', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'permenent_address', header: 'Permanent Address', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'course_name', header: 'Course Name', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'batch_name', header: 'Batch Name', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'scheme_name', header: 'Scheme Name', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'uniform_distribution', header: 'Uniform Distribution', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'study_material_distribution', header: 'Study Material Distribution', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'attendance_in_percentage', header: 'Attendance in %', Cell: ({ cell }) => formatPercentage(cell.getValue()) },
      { accessorKey: 'result_status', header: 'Result', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'is_placed', header: 'Placement Status', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'self_employed', header: 'Placement Type', Cell: ({ cell }) => formatPlacementType(cell.getValue()) },
      { accessorKey: 'company', header: 'Company', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'first_salary_amount', header: '1st Month Salary', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'second_salary_amount', header: '2nd Month Salary', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'third_salary_amount', header: '3rd Month Salary', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'fourth_salary_amount', header: '4th Month Salary', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'fifth_salary_amount', header: '5th Month Salary', Cell: ({ cell }) => formatText(cell.getValue()) },
      { accessorKey: 'sixth_salary_amount', header: '6th Month Salary', Cell: ({ cell }) => formatText(cell.getValue()) },
    ],
    [],
  );

  const rowVirtualizerInstanceRef = useRef(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    axios.post('/students/getByBatchId', {
        batch_id: id,
    })
      .then((res) => {
        setData(res.data);
        //console.log(res.data)
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  }, [handlePageUpdate, id]);

  useEffect(() => {
    try {
      // Scroll to the top of the table when the sorting changes
      rowVirtualizerInstanceRef.current?.scrollToIndex(0);
    } catch (e) {
      console.log(e);
    }
  }, [sorting]);

  const table = useMantineReactTable({
    data,
    columns,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enablePinning: true,
    enablePagination: true,
    enableMultiSort: true, 
    columnFilterDisplayMode:"popover",
    paginationDisplayMode:"pages",
    positionToolbarAlertBanner:"bottom",
    enableColumnResizing: true,
    enableColumnFilter: true,
    enableColumnSelection: true,
    enableGlobalFilter: true,
    enableGlobalFilterModes: true,
    enableStickyHeader: true,
    enableColumnSticky: true,
    enableRowNumbers: true,
    enableRowActions: true,
    renderRowActionMenuItems: ({ row }) => (
      <>
        {userType!=='Accounts'&&(<>
          <Menu.Item onClick={() => updateProductModalSetting(row.original)}>Edit</Menu.Item>
        <Menu.Item onClick={() => updateAttendanceSetting(row.original)}>Attendance</Menu.Item>
        <Menu.Item onClick={() => updatePlacementSetting(row.original)}>Placement</Menu.Item>
        </>)}
        <Menu.Item onClick={() => navigate(`/student/details`,{
          state: { id: row.original.student_id }, // Pass the row data as a prop to the Details component
        }, { replace: true })}>Details</Menu.Item>
      </>
    ),
  });

  return (
    <Paper shadow="sm" p="lg" withBorder >
      <div style={{ width: '100%' }}>
        <MantineReactTable
          table={table}
          striped
          highlightOnHover
          horizontalSpacing="md"
          verticalSpacing="sm"
          fontSize="sm"
          withColumnBorders
          borderRadius="md"
          withGlobalFilter
          height={500}
          rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
          enablePagination={true}
        />
      </div>
    </Paper>
  );
};

export default Example;
